import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogOut, Trash2, Plus, IndianRupee, Package, Ticket, ImagePlus, MessagesSquare, Users, Search, Upload, Settings, MapPin, FileText, FileDown } from "lucide-react";
import ImageListField from "@/components/ImageListField";

const STATUSES = ["placed", "confirmed", "packed", "shipped", "out_for_delivery", "delivered", "cancelled"];

// Shared file upload component
function FileField({ value, onChange, testId, accept = "image/*,application/pdf", label = "Upload file" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const doUpload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Paste URL or upload…" className="rounded-none flex-1" data-testid={testId} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
          className="btn-gold-outline px-3 py-2 text-[10px] uppercase tracking-widest flex items-center gap-2 disabled:opacity-50" data-testid={`${testId}-upload`}>
          <Upload size={12} /> {busy ? "Uploading…" : label}
        </button>
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
      </div>
      {value && (
        <img src={value.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${value}` : value} alt="preview" className="w-24 h-24 object-cover bg-cream-200 border hairline" />
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [stats, setStats] = useState({});
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [banners, setBanners] = useState([]);
  const [popups, setPopups] = useState([]);
  const [orderFilters, setOrderFilters] = useState({ status: "", payment_method: "", coupon: "", from_date: "", to_date: "", q: "" });

  const loadStats = async () => setStats((await api.get("/admin/stats")).data);
  const loadOrders = async () => {
    const params = Object.fromEntries(Object.entries(orderFilters).filter(([, v]) => v));
    setOrders((await api.get("/admin/orders", { params })).data);
  };
  const loadProducts = async () => setProducts((await api.get("/admin/products")).data);
  const loadCoupons = async () => setCoupons((await api.get("/admin/coupons")).data);
  const loadBanners = async () => setBanners((await api.get("/admin/banners")).data);
  const loadPopups = async () => setPopups((await api.get("/admin/popups")).data);
  const loadAll = async () => { await Promise.all([loadStats(), loadOrders(), loadProducts(), loadCoupons(), loadBanners(), loadPopups()]); };

  useEffect(() => {
    api.get("/admin/me").then(({ data }) => {
      if (!data.admin) { nav("/admin/login"); return; }
      setReady(true);
      loadAll().catch(() => toast.error("Failed to load"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const logout = async () => { await api.post("/admin/logout"); nav("/admin/login"); };

  if (!ready) return <div className="max-w-[1400px] mx-auto py-32 text-center text-ink-500">Loading admin…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="eyebrow text-gold-500">Admin console</div>
          <h1 className="mt-2 font-display text-4xl">IMMUSKI HQ</h1>
        </div>
        <button onClick={logout} className="btn-gold-outline px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="admin-logout">
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${(stats.revenue || 0).toFixed(0)}`} />
        <StatCard icon={Package} label="Orders" value={stats.orders_count || 0} />
        <StatCard icon={Ticket} label="Active subs" value={stats.active_subscriptions || 0} />
        <StatCard icon={Users} label="Customers" value={stats.users || 0} />
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-transparent border-b hairline rounded-none w-full justify-start h-auto p-0 gap-6 mb-6 overflow-x-auto no-scrollbar">
          <TT v="orders" label="Orders" />
          <TT v="products" label="Products" />
          <TT v="coupons" label="Coupons" />
          <TT v="banners" label="Ads / Carousel" />
          <TT v="popups" label="Popups" />
          <TT v="copy" label="Site copy" />
          <TT v="site" label="Site settings" />
          <TT v="invoice" label="Invoice" />
          <TT v="reviews" label="Reviews" />
        </TabsList>

        <TabsContent value="orders"><OrdersPanel orders={orders} filters={orderFilters} setFilters={setOrderFilters} apply={loadOrders} refresh={loadOrders} /></TabsContent>
        <TabsContent value="products"><ProductsPanel products={products} refresh={loadProducts} /></TabsContent>
        <TabsContent value="coupons"><CouponsPanel coupons={coupons} refresh={loadCoupons} /></TabsContent>
        <TabsContent value="banners"><BannersPanel banners={banners} refresh={loadBanners} /></TabsContent>
        <TabsContent value="popups"><PopupsPanel popups={popups} refresh={loadPopups} /></TabsContent>
        <TabsContent value="copy"><CopyPanel /></TabsContent>
        <TabsContent value="site"><SitePanel /></TabsContent>
        <TabsContent value="invoice"><InvoicePanel /></TabsContent>
        <TabsContent value="reviews"><ReviewsPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

const TT = ({ v, label }) => (
  <TabsTrigger value={v} data-testid={`admin-tab-${v}`} className="rounded-none border-b-2 border-transparent data-[state=active]:border-ink-900 data-[state=active]:bg-transparent px-0 pb-3 text-sm whitespace-nowrap">
    {label}
  </TabsTrigger>
);

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border hairline p-5 bg-white">
    <div className="flex justify-between items-start">
      <div className="eyebrow">{label}</div>
      <Icon size={16} className="text-gold-500" />
    </div>
    <div className="mt-3 font-display text-3xl text-ink-900">{value}</div>
  </div>
);

function OrdersPanel({ orders, filters, setFilters, apply, refresh }) {
  const [expanded, setExpanded] = useState(null);
  const [editingPay, setEditingPay] = useState(null);
  const [payDraft, setPayDraft] = useState({});

  const updateStatus = async (n, status) => {
    const eta = window.prompt("ETA (e.g. 12 Feb 2026) — blank keeps existing:");
    const note = window.prompt("Note (optional):") || "";
    try {
      await api.patch(`/admin/orders/${n}/status`, { status, eta: eta || undefined, note });
      toast.success("Status updated");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const savePayment = async (n) => {
    try {
      await api.patch(`/admin/orders/${n}/payment`, payDraft);
      setEditingPay(null); setPayDraft({});
      toast.success("Payment updated");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const reset = () => setFilters({ status: "", payment_method: "", coupon: "", from_date: "", to_date: "", q: "" });

  return (
    <>
      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 border hairline p-4 bg-white">
        <div className="col-span-2 md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
          <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Order #, email, phone, name, txn ID…" className="w-full h-10 pl-9 pr-3 border hairline text-sm" data-testid="orders-search" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-none h-10" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="rounded-none bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.payment_method || "all"} onValueChange={(v) => setFilters({ ...filters, payment_method: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-none h-10" data-testid="filter-pay"><SelectValue placeholder="Payment" /></SelectTrigger>
          <SelectContent className="rounded-none bg-white">
            <SelectItem value="all">All payments</SelectItem>
            <SelectItem value="cod">Cash on Delivery</SelectItem>
            <SelectItem value="stripe">Stripe</SelectItem>
            <SelectItem value="razorpay">Razorpay</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
          </SelectContent>
        </Select>
        <input type="date" value={filters.from_date} onChange={(e) => setFilters({ ...filters, from_date: e.target.value })} className="h-10 border hairline px-2 text-sm col-span-1" data-testid="filter-from" />
        <input type="date" value={filters.to_date} onChange={(e) => setFilters({ ...filters, to_date: e.target.value })} className="h-10 border hairline px-2 text-sm col-span-1" data-testid="filter-to" />
        <div className="col-span-2 md:col-span-6 flex flex-wrap gap-3 items-center">
          <button onClick={apply} className="btn-primary-ink px-6 py-2 text-xs uppercase tracking-widest" data-testid="apply-filters">Apply</button>
          <button onClick={() => { reset(); setTimeout(apply, 0); }} className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900" data-testid="reset-filters">Reset</button>
          <div className="ml-auto text-xs text-ink-500">{orders.length} result{orders.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      <div className="border hairline overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-cream-200">
            <tr className="text-left [&_th]:p-3 [&_th]:eyebrow">
              <th>Order</th><th>Date</th><th>Customer</th><th>Total</th><th>Payment</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <React.Fragment key={o.id}>
                <tr className="border-t hairline bg-white" data-testid={`admin-order-${o.order_number}`}>
                  <td className="p-3 font-mono text-xs">#{o.order_number}</td>
                  <td className="p-3 text-xs text-ink-500">{new Date(o.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <div>{o.address?.name}</div>
                    <div className="text-xs text-ink-500">{o.email}</div>
                  </td>
                  <td className="p-3 tabular-nums">₹{o.total.toFixed(0)}</td>
                  <td className="p-3 text-xs">
                    <div className="uppercase tracking-widest">{o.payment_method} · <span className="text-gold-500">{o.payment_status}</span></div>
                    {o.payment_transaction_id && <div className="text-ink-500 mt-1">Txn: <span className="font-mono">{o.payment_transaction_id}</span></div>}
                    {o.coupon_code && <div className="text-ink-500 mt-1">Coupon: {o.coupon_code}</div>}
                  </td>
                  <td className="p-3">
                    <Select value={o.status} onValueChange={(v) => updateStatus(o.order_number, v)}>
                      <SelectTrigger className="rounded-none h-9 w-40 border-ink-900/20 bg-white text-xs" data-testid={`status-select-${o.order_number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-none bg-white">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="link-underline text-xs">
                      {expanded === o.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr className="border-t hairline bg-cream-200">
                    <td colSpan={7} className="p-4 text-xs">
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <div className="eyebrow">Address</div>
                          <div className="mt-1">{o.address?.line1}{o.address?.line2 ? ", " + o.address.line2 : ""}</div>
                          <div>{o.address?.city}, {o.address?.state} {o.address?.pincode}</div>
                          <div className="text-ink-500">{o.address?.phone}</div>
                          {o.address?.latitude && (
                            <a href={`https://www.google.com/maps?q=${o.address.latitude},${o.address.longitude}`} target="_blank" rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 mt-2 text-gold-500 hover:text-ink-900" data-testid={`admin-map-link-${o.order_number}`}>
                              <MapPin size={11} /> Open in Google Maps ↗
                            </a>
                          )}
                        </div>
                        <div>
                          <div className="eyebrow">Items</div>
                          <ul className="mt-1 space-y-1">
                            {o.items.map((it, i) => <li key={i}>{it.name} × {it.quantity} — ₹{(it.price * it.quantity).toFixed(0)}{it.subscription ? ` (sub: ${it.subscription})` : ""}</li>)}
                          </ul>
                        </div>
                        <div>
                          <div className="eyebrow">Summary</div>
                          <div className="mt-1 flex justify-between"><span>Subtotal</span><span>₹{o.subtotal.toFixed(0)}</span></div>
                          <div className="flex justify-between"><span>Discount</span><span>– ₹{(o.discount || 0).toFixed(0)}</span></div>
                          <div className="flex justify-between"><span>Shipping</span><span>₹{(o.shipping || 0).toFixed(0)}</span></div>
                          <div className="flex justify-between font-medium border-t hairline pt-1 mt-1"><span>Total</span><span>₹{o.total.toFixed(0)}</span></div>
                          {o.eta && <div className="mt-2 text-ink-500">ETA: {o.eta}</div>}
                        </div>
                        <div>
                          <div className="eyebrow flex items-center justify-between">
                            Payment
                            {editingPay !== o.order_number && (
                              <button onClick={() => { setEditingPay(o.order_number); setPayDraft({ payment_method: o.payment_method, payment_status: o.payment_status, payment_transaction_id: o.payment_transaction_id || "", payment_notes: o.payment_notes || "" }); }}
                                className="text-[10px] link-underline">Edit</button>
                            )}
                          </div>
                          {editingPay === o.order_number ? (
                            <div className="space-y-2 mt-2">
                              <Select value={payDraft.payment_method} onValueChange={(v) => setPayDraft({ ...payDraft, payment_method: v })}>
                                <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`pay-method-${o.order_number}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none bg-white">
                                  {["cod", "stripe", "razorpay", "upi", "bank_transfer"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Select value={payDraft.payment_status} onValueChange={(v) => setPayDraft({ ...payDraft, payment_status: v })}>
                                <SelectTrigger className="rounded-none h-8 text-xs" data-testid={`pay-status-${o.order_number}`}><SelectValue /></SelectTrigger>
                                <SelectContent className="rounded-none bg-white">
                                  {["pending", "cod_pending", "paid", "failed", "refunded"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                </SelectContent>
                              </Select>
                              <Input placeholder="Transaction ID" value={payDraft.payment_transaction_id || ""} onChange={(e) => setPayDraft({ ...payDraft, payment_transaction_id: e.target.value })}
                                className="rounded-none h-8 text-xs" data-testid={`pay-txn-${o.order_number}`} />
                              <Input placeholder="Notes" value={payDraft.payment_notes || ""} onChange={(e) => setPayDraft({ ...payDraft, payment_notes: e.target.value })}
                                className="rounded-none h-8 text-xs" />
                              <div className="flex gap-2">
                                <button onClick={() => savePayment(o.order_number)} className="btn-primary-ink px-3 py-1 text-[10px] uppercase tracking-widest" data-testid={`pay-save-${o.order_number}`}>Save</button>
                                <button onClick={() => setEditingPay(null)} className="text-[10px] text-ink-500">Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 space-y-1">
                              <div>Method: <span className="uppercase">{o.payment_method}</span></div>
                              <div>Status: <span className="text-gold-500">{o.payment_status}</span></div>
                              {o.payment_transaction_id && <div>Txn ID: <span className="font-mono">{o.payment_transaction_id}</span></div>}
                              {o.payment_notes && <div className="text-ink-500">{o.payment_notes}</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-ink-500 text-sm">No orders match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ProductsPanel({ products, refresh }) {
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const filtered = q.trim() ? products.filter((p) => (p.name + " " + p.subtitle + " " + p.category).toLowerCase().includes(q.toLowerCase())) : products;
  return (
    <>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" className="w-full h-10 pl-9 pr-3 border hairline text-sm" data-testid="admin-product-search" />
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary-ink px-4 py-2 text-xs uppercase tracking-widest flex items-center gap-2" data-testid="admin-new-product">
          <Plus size={14} /> New product
        </button>
      </div>
      {creating && <NewProductForm onDone={() => { setCreating(false); refresh(); }} onCancel={() => setCreating(false)} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filtered.map((p) => <ProductEditor key={p.id} p={p} refresh={refresh} />)}
        {filtered.length === 0 && <div className="text-ink-500 text-sm p-8">No products found.</div>}
      </div>
    </>
  );
}

function NewProductForm({ onDone, onCancel }) {
  const [f, setF] = useState({
    slug: "", name: "", subtitle: "", category: "face", size: "50ml",
    price: 0, compare_at_price: "", hero_image: "", gallery: [],
    short_description: "", long_description: "", how_to_use: "", ingredients: "",
    stock: 100, subscription_discount_pct: 10,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.slug || !f.name || !f.price) return toast.error("Slug, name and price required");
    setSaving(true);
    try {
      const body = { ...f };
      body.price = parseFloat(body.price);
      body.compare_at_price = body.compare_at_price === "" ? null : parseFloat(body.compare_at_price);
      body.stock = parseInt(body.stock, 10);
      body.subscription_discount_pct = parseFloat(body.subscription_discount_pct);
      if (body.gallery && body.gallery.length > 0) body.hero_image = body.gallery[0];
      await api.post("/admin/products", body);
      toast.success("Product created");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setSaving(false);
  };
  return (
    <div className="border-2 border-ink-900 p-6 bg-cream-200 mb-6" data-testid="admin-new-product-form">
      <div className="eyebrow mb-4">Create new product</div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Slug*</Label><Input value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} placeholder="e.g. night-serum" className="rounded-none" data-testid="np-slug" /></div>
        <div><Label>Category*</Label>
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger className="rounded-none" data-testid="np-cat"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-white">
              <SelectItem value="face">Face</SelectItem><SelectItem value="hair">Hair</SelectItem>
              <SelectItem value="sun">Sun</SelectItem><SelectItem value="cleanser">Cleanser</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>Name*</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none" data-testid="np-name" /></div>
        <div className="col-span-2"><Label>Subtitle</Label><Input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className="rounded-none" /></div>
        <div><Label>Size</Label><Input value={f.size} onChange={(e) => setF({ ...f, size: e.target.value })} className="rounded-none" /></div>
        <div><Label>Stock</Label><Input type="number" value={f.stock} onChange={(e) => setF({ ...f, stock: e.target.value })} className="rounded-none" /></div>
        <div><Label>Price (₹)*</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-none" data-testid="np-price" /></div>
        <div><Label>MRP (₹)</Label><Input type="number" value={f.compare_at_price} onChange={(e) => setF({ ...f, compare_at_price: e.target.value })} className="rounded-none" /></div>
        <div className="col-span-2">
          <Label>Product images (upload multiple — first is the hero)</Label>
          <ImageListField
            value={f.gallery && f.gallery.length > 0 ? f.gallery : (f.hero_image ? [f.hero_image] : [])}
            onChange={(list) => setF({ ...f, gallery: list, hero_image: list[0] || "" })}
            testId="np-images"
          />
        </div>
        <div className="col-span-2"><Label>Short description</Label><Textarea value={f.short_description} onChange={(e) => setF({ ...f, short_description: e.target.value })} className="rounded-none" rows={2} /></div>
        <div className="col-span-2"><Label>Long description</Label><Textarea value={f.long_description} onChange={(e) => setF({ ...f, long_description: e.target.value })} className="rounded-none" rows={3} /></div>
      </div>
      <div className="mt-4 flex gap-3">
        <button onClick={save} disabled={saving} className="btn-primary-ink px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="np-save">
          {saving ? "Creating…" : "Create product"}
        </button>
        <button onClick={onCancel} className="text-xs uppercase tracking-widest text-ink-500">Cancel</button>
      </div>
    </div>
  );
}

function ProductEditor({ p, refresh }) {
  const [form, setForm] = useState({
    name: p.name, subtitle: p.subtitle, price: p.price,
    compare_at_price: p.compare_at_price || "",
    hero_image: p.hero_image,
    gallery: p.gallery && p.gallery.length > 0 ? p.gallery : (p.hero_image ? [p.hero_image] : []),
    stock: p.stock,
    short_description: p.short_description, long_description: p.long_description,
    how_to_use: p.how_to_use, ingredients: p.ingredients,
    subscription_discount_pct: p.subscription_discount_pct,
    active: p.active,
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const upd = { ...form };
      upd.price = parseFloat(upd.price);
      upd.compare_at_price = upd.compare_at_price === "" ? null : parseFloat(upd.compare_at_price);
      upd.stock = parseInt(upd.stock, 10);
      upd.subscription_discount_pct = parseFloat(upd.subscription_discount_pct);
      // Ensure gallery + hero stay in sync (hero = first image)
      if (upd.gallery && upd.gallery.length > 0) upd.hero_image = upd.gallery[0];
      await api.patch(`/admin/products/${p.id}`, upd);
      toast.success(`${p.name} updated`);
      refresh();
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };
  const del = async () => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success(`${p.name} deleted`);
      refresh();
    } catch { toast.error("Delete failed"); }
    setDeleting(false);
  };
  return (
    <div className="border hairline p-5 bg-white">
      <div className="flex gap-4">
        <img src={form.hero_image} alt="" className="w-24 h-32 object-cover bg-cream-200" />
        <div className="flex-1 space-y-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" />
          <Input value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} className="rounded-none" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price" className="rounded-none" data-testid={`edit-price-${p.slug}`} />
            <div className="relative">
              <Input type="number" value={form.compare_at_price} onChange={(e) => setForm({ ...form, compare_at_price: e.target.value })} placeholder="MRP (before discount)" className="rounded-none pr-16" data-testid={`edit-mrp-${p.slug}`} />
              {form.compare_at_price !== "" && form.compare_at_price !== null && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, compare_at_price: "" })}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest bg-ink-900 text-white px-2 py-1 hover:bg-black"
                  title="Remove discount — 'Save ₹X' badge will disappear from the product card"
                  data-testid={`remove-discount-${p.slug}`}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        <Label className="text-xs">Product images (first is the hero, drag order)</Label>
        <ImageListField
          value={form.gallery && form.gallery.length > 0 ? form.gallery : (form.hero_image ? [form.hero_image] : [])}
          onChange={(list) => setForm({ ...form, gallery: list, hero_image: list[0] || "" })}
          testId={`prod-images-${p.slug}`}
        />
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-xs">Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="rounded-none" /></div>
          <div><Label className="text-xs">Sub %</Label><Input type="number" value={form.subscription_discount_pct} onChange={(e) => setForm({ ...form, subscription_discount_pct: e.target.value })} className="rounded-none" /></div>
          <div><Label className="text-xs">Active</Label>
            <Select value={String(form.active)} onValueChange={(v) => setForm({ ...form, active: v === "true" })}>
              <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-white"><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <Label className="text-xs">Short description</Label>
        <Textarea value={form.short_description} onChange={(e) => setForm({ ...form, short_description: e.target.value })} className="rounded-none" rows={2} />
        <Label className="text-xs">Long description</Label>
        <Textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} className="rounded-none" rows={3} />
        <Label className="text-xs">How to use</Label>
        <Textarea value={form.how_to_use} onChange={(e) => setForm({ ...form, how_to_use: e.target.value })} className="rounded-none" rows={2} />
        <Label className="text-xs">Ingredients</Label>
        <Textarea value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="rounded-none" rows={3} />
      </div>
      <div className="mt-4 flex flex-wrap gap-3">
        <button onClick={save} disabled={saving} className="btn-primary-ink px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid={`prod-save-${p.slug}`}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button onClick={del} disabled={deleting} className="border border-destructive text-destructive px-4 py-2 text-xs uppercase tracking-widest hover:bg-destructive hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2" data-testid={`prod-delete-${p.slug}`}>
          <Trash2 size={12} /> {deleting ? "Deleting…" : "Delete"}
        </button>
      </div>
    </div>
  );
}

function CouponsPanel({ coupons, refresh }) {
  const [form, setForm] = useState({ code: "", kind: "percent", value: 10, min_subtotal: 0, valid_until: "", usage_limit: 0 });
  const create = async () => {
    if (!form.code || !form.valid_until) return toast.error("Code and expiry required");
    try {
      await api.post("/admin/coupons", { ...form, value: parseFloat(form.value), min_subtotal: parseFloat(form.min_subtotal), usage_limit: parseInt(form.usage_limit, 10) || 0, valid_until: new Date(form.valid_until).toISOString() });
      toast.success("Coupon created");
      setForm({ code: "", kind: "percent", value: 10, min_subtotal: 0, valid_until: "", usage_limit: 0 });
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/coupons/${id}`); toast.success("Deleted"); refresh(); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="border hairline p-6 bg-white">
        <div className="eyebrow">Create coupon</div>
        <div className="mt-4 space-y-3">
          <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="rounded-none uppercase" data-testid="coupon-code-input" /></div>
          <div><Label>Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="rounded-none" data-testid="coupon-kind"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none bg-white">
                <SelectItem value="percent">Percent %</SelectItem><SelectItem value="flat">Flat ₹</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Value {form.kind === "percent" ? "(%)" : "(₹)"}</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="rounded-none" data-testid="coupon-value" /></div>
          <div><Label>Minimum subtotal (₹)</Label><Input type="number" value={form.min_subtotal} onChange={(e) => setForm({ ...form, min_subtotal: e.target.value })} className="rounded-none" /></div>
          <div><Label>Valid until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="rounded-none" data-testid="coupon-expiry" /></div>
          <div><Label>Usage limit (0 = unlimited)</Label><Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className="rounded-none" /></div>
          <button onClick={create} className="btn-primary-ink w-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="create-coupon">
            <Plus size={14} /> Create coupon
          </button>
        </div>
      </div>
      <div className="md:col-span-2 space-y-3">
        {coupons.map((c) => (
          <div key={c.id} className="border hairline p-4 bg-white flex justify-between items-center" data-testid={`coupon-row-${c.code}`}>
            <div>
              <div className="font-mono tracking-widest">{c.code}</div>
              <div className="text-xs text-ink-500 mt-1">
                {c.kind === "percent" ? `${c.value}% off` : `₹${c.value} off`} · min ₹{c.min_subtotal} · used {c.used_count}{c.usage_limit ? `/${c.usage_limit}` : ""}
              </div>
              <div className="text-xs text-ink-500">Valid till {new Date(c.valid_until).toLocaleDateString()}</div>
            </div>
            <button onClick={() => del(c.id)} className="text-ink-500 hover:text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
        {coupons.length === 0 && <div className="text-ink-500 text-sm text-center py-8">No coupons yet.</div>}
      </div>
    </div>
  );
}

function BannersPanel({ banners, refresh }) {
  const [form, setForm] = useState({ image_url: "", headline: "", subhead: "", cta_label: "", cta_link: "/shop", sort_order: 0 });
  const create = async () => {
    if (!form.image_url) return toast.error("Image required");
    try {
      await api.post("/admin/banners", { ...form, sort_order: parseInt(form.sort_order, 10) || 0 });
      toast.success("Ad added");
      setForm({ image_url: "", headline: "", subhead: "", cta_label: "", cta_link: "/shop", sort_order: 0 });
      refresh();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/banners/${id}`); refresh(); toast.success("Deleted"); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="border hairline p-6 bg-white">
        <div className="eyebrow">New carousel ad</div>
        <div className="mt-4 space-y-3">
          <div><Label>Image (upload or URL)</Label><FileField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="banner-image" /></div>
          <div><Label>Headline</Label><Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} className="rounded-none" /></div>
          <div><Label>Subhead</Label><Input value={form.subhead} onChange={(e) => setForm({ ...form, subhead: e.target.value })} className="rounded-none" /></div>
          <div><Label>CTA label</Label><Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} className="rounded-none" /></div>
          <div><Label>CTA link</Label><Input value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} className="rounded-none" /></div>
          <div><Label>Sort order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="rounded-none" /></div>
          <button onClick={create} className="btn-primary-ink w-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="create-banner">
            <ImagePlus size={14} /> Add banner
          </button>
        </div>
      </div>
      <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {banners.map((b) => (
          <div key={b.id} className="border hairline overflow-hidden bg-white">
            <div className="aspect-video bg-ink-900"><img src={b.image_url.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${b.image_url}` : b.image_url} alt="" className="w-full h-full object-cover" /></div>
            <div className="p-3 flex justify-between">
              <div><div className="font-medium text-sm">{b.headline || "—"}</div><div className="text-xs text-ink-500">{b.subhead}</div></div>
              <button onClick={() => del(b.id)} className="text-ink-500 hover:text-destructive"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PopupsPanel({ popups, refresh }) {
  const [form, setForm] = useState({ title: "", body: "", image_url: "", coupon_code: "", cta_label: "Copy code", show_after_seconds: 8, active: true });
  const create = async () => {
    if (!form.title || !form.body) return toast.error("Title and body required");
    try {
      await api.post("/admin/popups", { ...form, show_after_seconds: parseInt(form.show_after_seconds, 10) });
      toast.success("Popup published");
      setForm({ title: "", body: "", image_url: "", coupon_code: "", cta_label: "Copy code", show_after_seconds: 8, active: true });
      refresh();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await api.delete(`/admin/popups/${id}`); refresh(); };
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="border hairline p-6 bg-white">
        <div className="eyebrow">New popup</div>
        <div className="mt-4 space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none" data-testid="popup-title" /></div>
          <div><Label>Body</Label><Textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="rounded-none" rows={3} /></div>
          <div><Label>Thumbnail image (optional)</Label><FileField value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="popup-image" accept="image/*" label="Upload image" /></div>
          <div><Label>Coupon code (optional)</Label><Input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} className="rounded-none uppercase" /></div>
          <div><Label>Show after (seconds)</Label><Input type="number" value={form.show_after_seconds} onChange={(e) => setForm({ ...form, show_after_seconds: e.target.value })} className="rounded-none" /></div>
          <button onClick={create} className="btn-primary-ink w-full py-3 text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="create-popup">
            <MessagesSquare size={14} /> Publish popup
          </button>
        </div>
      </div>
      <div className="md:col-span-2 space-y-3">
        {popups.map((p) => (
          <div key={p.id} className="border hairline p-4 bg-white flex justify-between items-start gap-3">
            {p.image_url && <img src={p.image_url.startsWith("/api/") ? `${API.replace(/\/api$/, "")}${p.image_url}` : p.image_url} alt="" className="w-16 h-16 object-cover bg-cream-200" />}
            <div className="flex-1">
              <div className="font-display text-lg">{p.title}</div>
              <div className="text-xs text-ink-500 mt-1">{p.body}</div>
              {p.coupon_code && <div className="mt-1 text-xs">Code: <span className="font-mono">{p.coupon_code}</span></div>}
              <div className="text-[10px] mt-1 uppercase tracking-widest text-olive-500">{p.active ? "Active" : "Inactive"}</div>
            </div>
            <button onClick={() => del(p.id)} className="text-ink-500 hover:text-destructive"><Trash2 size={14} /></button>
          </div>
        ))}
        {popups.length === 0 && <div className="text-ink-500 text-sm text-center py-8">No popups yet.</div>}
      </div>
    </div>
  );
}

function CopyPanel() {
  const FIELDS = [
    { section: "Hero", items: [
      ["hero_eyebrow", "Eyebrow (small caps line above heading)", "input"],
      ["hero_title_1", "Heading — line 1", "input"],
      ["hero_title_accent", "Heading — gold italic word", "input"],
      ["hero_title_2", "Heading — rest of line 2", "input"],
      ["hero_body", "Body paragraph", "textarea"],
      ["hero_cta_primary", "Primary CTA label", "input"],
      ["hero_cta_secondary", "Secondary CTA label", "input"],
      ["hero_badge_featured_label", "Photo badge — label (top-left)", "input"],
      ["hero_badge_featured_text", "Photo badge — text", "input"],
      ["hero_badge_from_label", "Price badge — 'From' label", "input"],
      ["hero_badge_from_price", "Price badge — price", "input"],
    ]},
    { section: "Collection", items: [
      ["collection_eyebrow", "Eyebrow", "input"],
      ["collection_title", "Section title", "input"],
    ]},
    { section: "Our story", items: [
      ["story_eyebrow", "Eyebrow", "input"],
      ["story_title_prefix", "Title — first sentence", "input"],
      ["story_title_accent", "Title — gold italic sentence", "input"],
      ["story_body", "Body paragraph", "textarea"],
      ["story_stat_1_num", "Stat 1 — number", "input"],
      ["story_stat_1_label", "Stat 1 — label", "input"],
      ["story_stat_2_num", "Stat 2 — number", "input"],
      ["story_stat_2_label", "Stat 2 — label", "input"],
      ["story_stat_3_num", "Stat 3 — number", "input"],
      ["story_stat_3_label", "Stat 3 — label", "input"],
    ]},
    { section: "Specialties banner (between Quiz and Story)", items: [
      ["specialties_eyebrow", "Eyebrow (small gold caps)", "input"],
      ["specialties_title_prefix", "Title — first sentence", "input"],
      ["specialties_title_accent", "Title — gold italic sentence", "input"],
      ["specialties_body", "Intro paragraph", "textarea"],
      ["specialty_1_num", "Card 1 — big number/word", "input"],
      ["specialty_1_title", "Card 1 — title", "input"],
      ["specialty_1_body", "Card 1 — body", "textarea"],
      ["specialty_2_num", "Card 2 — big number/word", "input"],
      ["specialty_2_title", "Card 2 — title", "input"],
      ["specialty_2_body", "Card 2 — body", "textarea"],
      ["specialty_3_num", "Card 3 — big number/word", "input"],
      ["specialty_3_title", "Card 3 — title", "input"],
      ["specialty_3_body", "Card 3 — body", "textarea"],
      ["specialty_4_num", "Card 4 — big number/word", "input"],
      ["specialty_4_title", "Card 4 — title", "input"],
      ["specialty_4_body", "Card 4 — body", "textarea"],
      ["specialties_cta_primary", "Primary CTA label", "input"],
      ["specialties_cta_secondary", "Secondary CTA label", "input"],
    ]},
    { section: "About Us page", items: [
      ["about_subtitle", "Eyebrow line", "input"],
      ["about_title", "Page title", "input"],
      ["about_body", "Body paragraph", "textarea"],
      ["about_mission_title", "Mission heading", "input"],
      ["about_mission_body", "Mission statement", "textarea"],
    ]},
    { section: "Contact Us page", items: [
      ["contact_title", "Page title", "input"],
      ["contact_subtitle", "Subtitle / tagline", "input"],
      ["contact_support_line", "Support intro line", "textarea"],
      ["contact_email", "Email address", "input"],
      ["contact_phone", "Phone (with country code)", "input"],
      ["contact_whatsapp", "WhatsApp (with country code)", "input"],
      ["contact_address", "Full address", "textarea"],
      ["contact_hours", "Working hours", "input"],
    ]},
    { section: "Talk to a Dermatologist page", items: [
      ["derm_title", "Page title", "input"],
      ["derm_subtitle", "Subtitle", "input"],
      ["derm_body", "Body paragraph", "textarea"],
      ["derm_cta_label", "WhatsApp button label", "input"],
    ]},
    { section: "Return & Refund policy page", items: [
      ["returns_title", "Page title", "input"],
      ["returns_subtitle", "Eyebrow / subtitle", "input"],
      ["returns_body", "Policy body (supports line breaks)", "textarea"],
    ]},
    { section: "Footer", items: [
      ["footer_about", "About paragraph", "textarea"],
      ["footer_contact_email", "Contact email", "input"],
      ["footer_contact_instagram", "Instagram handle", "input"],
    ]},
  ];
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    // Cache-buster so proxies / browsers never serve a stale copy of this endpoint
    api.get(`/settings/copy?_ts=${Date.now()}`)
      .then(({ data }) => setValues(data || {}))
      .catch((e) => setLoadError(e?.message || "Failed to load site copy"));
  }, []);
  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings/copy", { values });
      toast.success("Site copy saved");
    } catch { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <div className="max-w-4xl">
      <div className="border hairline p-6 bg-white mb-4">
        <div className="eyebrow">Edit any text shown on the storefront</div>
        <p className="mt-2 text-xs text-ink-500">Changes go live immediately. Fields left blank fall back to the built-in defaults.</p>
        {loadError && (
          <p className="mt-3 text-xs text-red-600" data-testid="copy-load-error">Couldn&apos;t load current copy: {loadError}</p>
        )}
      </div>
      {FIELDS.map((sec) => (
        <div key={sec.section} className="border hairline p-6 bg-white mb-4">
          <div className="font-display text-lg mb-4">{sec.section}</div>
          <div className="space-y-3">
            {sec.items.map(([k, label, kind]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                {kind === "textarea" ? (
                  <Textarea value={values[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-none" rows={3} data-testid={`copy-${k}`} />
                ) : (
                  <Input value={values[k] || ""} onChange={(e) => set(k, e.target.value)} className="rounded-none" data-testid={`copy-${k}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={save} disabled={busy} className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-site-copy">
        {busy ? "Saving…" : "Save all copy"}
      </button>
    </div>
  );
}

function SitePanel() {
  const [announcement, setAnnouncement] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [images, setImages] = useState({ hero_image: "", hero_images: [], story_image: "" });
  const [waNotify, setWaNotify] = useState({
    enabled: false, admin_phone: "", api_key: "",
    webhook_customer_order: "", webhook_admin_order: "", webhook_customer_status: "",
  });
  const [testPhone, setTestPhone] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/settings/announcement").then(({ data }) => setAnnouncement(data.text || ""));
    api.get("/settings/whatsapp").then(({ data }) => setWhatsapp(data.phone || ""));
    api.get("/settings/site-images").then(({ data }) => setImages({
      hero_image: data.hero_image || "",
      hero_images: data.hero_images || [],
      story_image: data.story_image || "",
    }));
    api.get("/admin/settings/whatsapp-notify").then(({ data }) => setWaNotify({
      enabled: !!data.enabled,
      admin_phone: data.admin_phone || "",
      api_key: data.api_key || "",
      webhook_customer_order: data.webhook_customer_order || "",
      webhook_admin_order: data.webhook_admin_order || "",
      webhook_customer_status: data.webhook_customer_status || "",
    })).catch(() => {});
  }, []);
  const saveWaNotify = async () => {
    setBusy(true);
    try { await api.patch("/admin/settings/whatsapp-notify", waNotify); toast.success("WhatsApp notifications saved"); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  const sendTest = async (which) => {
    try {
      const { data } = await api.post("/admin/settings/whatsapp-notify/test", { which, phone: testPhone || waNotify.admin_phone });
      toast.success(`Test sent → ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed — check webhook URL");
    }
  };
  const saveAnn = async () => {
    setBusy(true);
    try { await api.patch("/admin/settings/announcement", { text: announcement }); toast.success("Announcement saved"); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  const saveWa = async () => {
    setBusy(true);
    try { await api.patch("/admin/settings/whatsapp", { text: whatsapp }); toast.success("WhatsApp phone saved"); }
    catch { toast.error("Failed"); }
    setBusy(false);
  };
  const saveImg = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/settings/site-images", images);
      toast.success("Home images saved");
    } catch { toast.error("Failed"); }
    setBusy(false);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
      <div className="border hairline p-6 bg-white">
        <div className="eyebrow flex items-center gap-2"><Settings size={12} /> Top announcement bar</div>
        <div className="mt-4 space-y-3">
          <Textarea value={announcement} onChange={(e) => setAnnouncement(e.target.value)} className="rounded-none" rows={3} data-testid="site-announcement" />
          <p className="text-[11px] text-ink-500">Shown as the black bar at the top of every page.</p>
          <button onClick={saveAnn} disabled={busy} className="btn-primary-ink px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-announcement">Save</button>
        </div>
      </div>
      <div className="border hairline p-6 bg-white">
        <div className="eyebrow">WhatsApp phone (E.164 without +)</div>
        <div className="mt-4 space-y-3">
          <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="919999999999" className="rounded-none" data-testid="site-whatsapp" />
          <p className="text-[11px] text-ink-500">Used by the floating WhatsApp button on the storefront.</p>
          <button onClick={saveWa} disabled={busy} className="btn-primary-ink px-6 py-2 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-whatsapp">Save</button>
        </div>
      </div>
      <div className="border hairline p-6 bg-white md:col-span-2">
        <div className="eyebrow">Home hero carousel</div>
        <p className="mt-2 text-[11px] text-ink-500">Add one or more images. They rotate on the right side of the top hero every ~5 seconds. Drag order with the arrows.</p>
        <div className="mt-4">
          <ImageListField
            value={images.hero_images}
            onChange={(list) => setImages({ ...images, hero_images: list, hero_image: list[0] || images.hero_image })}
            testId="site-hero-images"
          />
        </div>
      </div>
      <div className="border hairline p-6 bg-white md:col-span-2">
        <div className="eyebrow">Story image</div>
        <div className="mt-4">
          <ImageListField
            value={images.story_image ? [images.story_image] : []}
            onChange={(list) => setImages({ ...images, story_image: list[0] || "" })}
            testId="site-story-image"
          />
        </div>
      </div>
      <div className="md:col-span-2">
        <button onClick={saveImg} disabled={busy} className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50" data-testid="save-site-images">
          {busy ? "Saving…" : "Save all home images"}
        </button>
      </div>

      <div className="border hairline p-6 bg-white md:col-span-2" data-testid="wa-notify-section">
        <div className="eyebrow flex items-center gap-2"><MessagesSquare size={12} /> WhatsApp order notifications (Botbiz)</div>
        <p className="mt-2 text-[11px] text-ink-500 leading-relaxed">
          Botbiz uses one Webhook Callback URL per approved template. Create a template in Botbiz → Bot Manager → Message Template, then wire it in Webhook Workflow and paste the generated URL below.
          Payload keys available for template variables: <code className="text-[10px] bg-ink-50 px-1">customer_name, order_number, order_total, order_status, items_summary, eta, address_line, track_url</code>.
        </p>
        <div className="mt-4 flex items-center gap-3">
          <input type="checkbox" id="wa-enabled" checked={waNotify.enabled}
            onChange={(e) => setWaNotify({ ...waNotify, enabled: e.target.checked })} data-testid="wa-enabled" />
          <Label htmlFor="wa-enabled" className="text-xs cursor-pointer">Enable WhatsApp notifications</Label>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Admin WhatsApp (E.164 without +)</Label>
            <Input value={waNotify.admin_phone}
              onChange={(e) => setWaNotify({ ...waNotify, admin_phone: e.target.value })}
              placeholder="919999999999" className="rounded-none" data-testid="wa-admin-phone" />
          </div>
          <div>
            <Label className="text-xs">Botbiz API key (optional)</Label>
            <Input value={waNotify.api_key}
              onChange={(e) => setWaNotify({ ...waNotify, api_key: e.target.value })}
              placeholder="24140|xxxxxxxxxxxxxxxxx" className="rounded-none" data-testid="wa-api-key" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Webhook — Order placed (to customer)</Label>
            <div className="flex gap-2">
              <Input value={waNotify.webhook_customer_order}
                onChange={(e) => setWaNotify({ ...waNotify, webhook_customer_order: e.target.value })}
                placeholder="https://dash.botbiz.io/webhook/..." className="rounded-none" data-testid="wa-webhook-customer-order" />
              <button type="button" onClick={() => sendTest("webhook_customer_order")}
                className="border hairline px-3 text-[10px] uppercase tracking-widest hover:bg-ink-900 hover:text-white"
                data-testid="wa-test-customer-order">Test</button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Webhook — Order placed (to admin)</Label>
            <div className="flex gap-2">
              <Input value={waNotify.webhook_admin_order}
                onChange={(e) => setWaNotify({ ...waNotify, webhook_admin_order: e.target.value })}
                placeholder="https://dash.botbiz.io/webhook/..." className="rounded-none" data-testid="wa-webhook-admin-order" />
              <button type="button" onClick={() => sendTest("webhook_admin_order")}
                className="border hairline px-3 text-[10px] uppercase tracking-widest hover:bg-ink-900 hover:text-white"
                data-testid="wa-test-admin-order">Test</button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Webhook — Order status update (to customer)</Label>
            <div className="flex gap-2">
              <Input value={waNotify.webhook_customer_status}
                onChange={(e) => setWaNotify({ ...waNotify, webhook_customer_status: e.target.value })}
                placeholder="https://dash.botbiz.io/webhook/..." className="rounded-none" data-testid="wa-webhook-customer-status" />
              <button type="button" onClick={() => sendTest("webhook_customer_status")}
                className="border hairline px-3 text-[10px] uppercase tracking-widest hover:bg-ink-900 hover:text-white"
                data-testid="wa-test-customer-status">Test</button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Test phone (optional — defaults to Admin WhatsApp)</Label>
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)}
              placeholder="919999999999" className="rounded-none" data-testid="wa-test-phone" />
          </div>
        </div>
        <div className="mt-5">
          <button onClick={saveWaNotify} disabled={busy}
            className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50"
            data-testid="save-wa-notify">
            {busy ? "Saving…" : "Save WhatsApp settings"}
          </button>
        </div>
        <details className="mt-5 text-[11px] text-ink-500 leading-relaxed">
          <summary className="cursor-pointer uppercase tracking-widest text-[10px] text-ink-900">How to get the webhook URL from Botbiz</summary>
          <ol className="mt-3 space-y-1 list-decimal pl-5">
            <li>Log in to <a href="https://dash.botbiz.io" target="_blank" rel="noreferrer" className="link-underline">dash.botbiz.io</a>.</li>
            <li>Go to Bot Manager → <strong>Message Template</strong>. Create and submit a template for each event (e.g. <code>order_placed_customer</code>, <code>order_placed_admin</code>, <code>order_status_customer</code>). Use variables like <code>&#123;&#123;1&#125;&#125;</code>, <code>&#123;&#123;2&#125;&#125;</code>…</li>
            <li>Wait for WhatsApp to <strong>approve</strong> each template.</li>
            <li>Go to <strong>Webhook Workflow</strong>, click <em>Create</em>, give it a name, select your WhatsApp bot account, pick the approved template, then click <em>Create Workflow</em>.</li>
            <li>Copy the <strong>Webhook Callback URL</strong> and paste it in the matching field above.</li>
            <li>Click <em>Test</em> next to the URL — Botbiz will show the received payload. Map each template variable to a payload key (e.g. map <code>&#123;&#123;1&#125;&#125;</code> → <code>customer_name</code>).</li>
            <li>Save the workflow in Botbiz, then click <em>Save WhatsApp settings</em> here.</li>
          </ol>
        </details>
      </div>
    </div>
  );
}

const INVOICE_FIELDS = [
  { section: "Business identity", cols: 2, fields: [
    { k: "company_name", label: "Brand name", placeholder: "IMMUSKI" },
    { k: "legal_name", label: "Legal entity name", placeholder: "Swasth Derma Labs" },
    { k: "gst_number", label: "GSTIN", placeholder: "07AAAAA0000A1Z5" },
    { k: "pan", label: "PAN", placeholder: "AAAAA0000A" },
    { k: "state", label: "State", placeholder: "Delhi" },
    { k: "state_code", label: "State code", placeholder: "07" },
  ]},
  { section: "Registered address", cols: 1, fields: [
    { k: "address_line1", label: "Address line 1", placeholder: "SHOP 1, RZ-14, Sukhi Ram Park…" },
    { k: "address_line2", label: "Address line 2", placeholder: "Uttam Nagar, New Delhi - 110059" },
  ]},
  { section: "Contact", cols: 2, fields: [
    { k: "phone", label: "Phone" },
    { k: "email", label: "Email" },
  ]},
  { section: "Tax & numbering", cols: 3, fields: [
    { k: "hsn", label: "Default HSN code", placeholder: "3304" },
    { k: "gst_rate", label: "GST rate (%)", placeholder: "18", type: "number" },
    { k: "invoice_prefix", label: "Invoice number prefix", placeholder: "IMM/25-26/" },
  ]},
  { section: "Bank details (optional)", cols: 3, fields: [
    { k: "bank_name", label: "Bank name" },
    { k: "bank_account", label: "A/c number" },
    { k: "bank_ifsc", label: "IFSC" },
  ]},
  { section: "Footer", cols: 1, fields: [
    { k: "signature_note", label: "Signature note" },
    { k: "terms", label: "Terms & conditions", type: "textarea" },
  ]},
];

function InvoicePanel() {
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [previewOrder, setPreviewOrder] = useState("");

  useEffect(() => {
    api.get("/admin/settings/invoice").then(({ data }) => setValues(data));
    api.get("/admin/orders").then(({ data }) => {
      if (data && data[0]) setPreviewOrder(data[0].order_number);
    }).catch(() => {});
  }, []);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...values, gst_rate: parseFloat(values.gst_rate) || 18 };
      const { data } = await api.patch("/admin/settings/invoice", payload);
      setValues(data.value);
      toast.success("Invoice settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
    setBusy(false);
  };

  if (!values) return <div className="text-ink-500 text-sm">Loading invoice settings…</div>;

  return (
    <div className="max-w-5xl space-y-8">
      <div className="border hairline p-5 bg-cream-50 flex flex-wrap gap-4 items-center justify-between">
        <div>
          <div className="eyebrow flex items-center gap-2"><FileText size={12} /> GST Invoice settings</div>
          <p className="mt-2 text-[12px] text-ink-500 max-w-2xl">
            These fields print on every generated tax invoice. Prices are treated as GST-inclusive — CGST/SGST are back-calculated using the rate below.
          </p>
        </div>
        {previewOrder && (
          <a
            href={`${API}/orders/${encodeURIComponent(previewOrder)}/invoice.pdf`}
            target="_blank" rel="noopener noreferrer"
            className="btn-gold-outline px-4 py-2 text-[11px] uppercase tracking-widest inline-flex items-center gap-2"
            data-testid="preview-invoice-btn"
          >
            <FileDown size={12} /> Preview latest invoice
          </a>
        )}
      </div>

      {INVOICE_FIELDS.map((section) => (
        <div key={section.section} className="border hairline p-6 bg-white">
          <div className="eyebrow">{section.section}</div>
          <div className={`mt-4 grid gap-4 ${section.cols === 3 ? "grid-cols-1 md:grid-cols-3" : section.cols === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}>
            {section.fields.map((f) => (
              <div key={f.k}>
                <Label className="text-[11px] uppercase tracking-widest text-ink-500">{f.label}</Label>
                {f.type === "textarea" ? (
                  <Textarea
                    value={values[f.k] || ""}
                    onChange={(e) => set(f.k, e.target.value)}
                    className="rounded-none mt-1"
                    rows={3}
                    data-testid={`invoice-${f.k}`}
                  />
                ) : (
                  <Input
                    value={values[f.k] ?? ""}
                    onChange={(e) => set(f.k, e.target.value)}
                    placeholder={f.placeholder || ""}
                    type={f.type || "text"}
                    className="rounded-none mt-1"
                    data-testid={`invoice-${f.k}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex gap-3">
        <button
          onClick={save}
          disabled={busy}
          className="btn-primary-ink px-6 py-3 text-xs uppercase tracking-widest disabled:opacity-50"
          data-testid="save-invoice-settings"
        >
          {busy ? "Saving…" : "Save invoice settings"}
        </button>
      </div>
    </div>
  );
}

function ReviewsPanel() {
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const load = () => api.get("/admin/reviews").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const reply = async (id) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.patch(`/admin/reviews/${id}`, { reply: drafts[id] || "" });
      toast.success("Reply saved");
      setDrafts((d) => ({ ...d, [id]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy((b) => ({ ...b, [id]: false }));
  };
  const del = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    await api.delete(`/admin/reviews/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="border hairline p-5 bg-cream-50">
        <div className="eyebrow">Customer reviews</div>
        <p className="mt-2 text-[12px] text-ink-500">Reply as IMMUSKI or remove abusive content. Replies show under each review on the product page.</p>
      </div>
      {rows.length === 0 && <div className="text-sm text-ink-500">No reviews yet.</div>}
      {rows.map((r) => (
        <div key={r.id} className="border hairline p-5 bg-white" data-testid={`admin-review-${r.id}`}>
          <div className="flex justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs text-ink-500">{r.product_name}</div>
              <div className="mt-1 text-sm">
                {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}
                {r.title && <span className="ml-2 font-medium">{r.title}</span>}
              </div>
              <div className="mt-1 text-[11px] text-ink-500">{r.user_name} · {new Date(r.created_at).toLocaleString()}</div>
            </div>
            <button onClick={() => del(r.id)} className="text-[11px] uppercase tracking-widest text-red-600 hover:text-red-800" data-testid={`review-delete-${r.id}`}>Delete</button>
          </div>
          <p className="mt-3 text-sm text-ink-700 whitespace-pre-line">{r.body}</p>
          {r.admin_reply && (
            <div className="mt-3 border-l-2 border-gold-500 pl-3 py-1 bg-cream-50 text-[13px] whitespace-pre-line">
              <span className="eyebrow text-gold-500 block">Current reply · {r.admin_replied_at && new Date(r.admin_replied_at).toLocaleDateString()}</span>
              {r.admin_reply}
            </div>
          )}
          <div className="mt-3 flex gap-2 items-start">
            <textarea
              value={drafts[r.id] ?? r.admin_reply ?? ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
              rows={2}
              placeholder="Write a reply as IMMUSKI…"
              className="flex-1 border hairline p-2 text-sm bg-white"
              data-testid={`review-reply-input-${r.id}`}
            />
            <button onClick={() => reply(r.id)} disabled={busy[r.id]} className="btn-primary-ink px-4 py-2 text-[11px] uppercase tracking-widest disabled:opacity-50" data-testid={`review-reply-save-${r.id}`}>
              {busy[r.id] ? "Saving…" : "Save reply"}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
